import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileUp, Plus, Sparkles, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { friendlyMessage } from '@/api/http'
import { genreTemplateApi } from '@/api/genreTemplateApi'
import { projectApi } from '@/api/projectApi'
import { Badge } from '@/components/ui/badge'
import { ProjectStatusBadge } from '@/components/project/ProjectStatusBadge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { OnboardingChecklist } from '@/components/onboarding/OnboardingChecklist'
import { CreateProjectWizard } from '@/components/project/CreateProjectWizard'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Pagination } from '@/components/ui/pagination'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { resolveProjectGenreLabel } from '@/lib/projectGenreLabel'
import { useAuthStore } from '@/stores/authStore'
import { useUiStore } from '@/stores/uiStore'
import type { Project } from '@/types/nf'
import { formatCount, formatNumber } from '@/utils/format'

export const PROJECT_LIST_PAGE_SIZE = 12
export type ProjectSortKey = 'updated' | 'title' | 'words' | 'chapters'

interface ProjectSortOption {
  value: ProjectSortKey
  labelKey: string
}

export function getProjectSortOptions(projects: Project[]): ProjectSortOption[] {
  const options: ProjectSortOption[] = [
    { value: 'updated', labelKey: 'project.sort.updated' },
    { value: 'title', labelKey: 'project.sort.title' },
  ]

  if (projects.some((project) => typeof project.current_words === 'number')) {
    options.push({ value: 'words', labelKey: 'project.sort.words' })
  }
  if (projects.some((project) => typeof project.chapter_count === 'number')) {
    options.push({ value: 'chapters', labelKey: 'project.sort.chapters' })
  }

  return options
}

export function sortProjects(projects: Project[], sortKey: ProjectSortKey) {
  const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true })
  return [...projects].sort((a, b) => {
    if (sortKey === 'title') return collator.compare(a.title, b.title)
    if (sortKey === 'words') return (b.current_words ?? 0) - (a.current_words ?? 0)
    if (sortKey === 'chapters') return (b.chapter_count ?? 0) - (a.chapter_count ?? 0)
    return (b.updated_at ?? 0) - (a.updated_at ?? 0)
  })
}

export function paginateProjects(projects: Project[], page: number, pageSize = PROJECT_LIST_PAGE_SIZE) {
  const start = page * pageSize
  return projects.slice(start, start + pageSize)
}

function formatWords(current?: number | null, target?: number | null) {
  return target ? `${formatNumber(current ?? 0)} / ${formatNumber(target)}` : formatNumber(current ?? 0)
}

export function ProjectCard({
  project,
  genreLabel,
  onOpen,
  onDelete,
}: {
  project: Project
  genreLabel: string
  onOpen: (id: string) => void
  onDelete: (project: Project) => void
}) {
  const { t } = useTranslation()
  return (
    <Card
      className="group flex h-full cursor-pointer flex-col rounded-lg py-0 transition-colors hover:border-primary/55 hover:bg-card/90 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(project.id)}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onOpen(project.id)
        }
      }}
    >
      <CardHeader className="gap-3 p-5">
        <div className="flex min-h-6 items-start justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            {genreLabel ? <Badge variant="secondary">{genreLabel}</Badge> : null}
            {project.status ? <ProjectStatusBadge status={project.status} /> : null}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="-mr-2 -mt-2 size-8 text-muted-foreground hover:text-destructive"
            aria-label={t('project.delete')}
            onClick={(event) => {
              event.stopPropagation()
              onDelete(project)
            }}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
        <div className="space-y-2">
          <CardTitle className="line-clamp-2 text-lg leading-snug group-hover:text-primary">{project.title}</CardTitle>
          <CardDescription className="line-clamp-2 min-h-10">
            {project.description?.trim() || project.logline?.trim() || t('project.noDescription')}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="mt-auto grid grid-cols-2 gap-3 border-t px-5 py-4 text-sm">
        <div>
          <p className="text-xs text-muted-foreground">{t('project.chapters')}</p>
          <p className="nf-mono mt-1">{formatCount(project.chapter_count ?? 0)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{t('project.words')}</p>
          <p className="nf-mono mt-1">{formatWords(project.current_words, project.target_words)}</p>
        </div>
      </CardContent>
    </Card>
  )
}

function ProjectSkeletonGrid() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <Card key={index} className="rounded-lg py-0">
          <CardHeader className="p-5">
            <div className="flex gap-2">
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 border-t px-5 py-4">
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-24" />
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

export default function ProjectList() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const currentBranchId = useUiStore((s) => s.currentBranchId)
  const [open, setOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null)
  const [sortKey, setSortKey] = useState<ProjectSortKey>('updated')
  const [page, setPage] = useState(0)

  const projectsQuery = useQuery({
    queryKey: ['projects', user?.user_id, currentBranchId],
    queryFn: () => projectApi.list(user!.user_id),
    enabled: Boolean(user?.user_id),
  })

  const templatesQuery = useQuery({
    queryKey: ['genre-templates'],
    queryFn: genreTemplateApi.list,
  })

  const lang = i18n.resolvedLanguage ?? i18n.language
  const projects = projectsQuery.data ?? []
  const sortOptions = useMemo(() => getProjectSortOptions(projects), [projects])

  const sortedProjects = useMemo(
    () => sortProjects(projects, sortKey),
    [projects, sortKey],
  )
  const pageCount = Math.max(1, Math.ceil(sortedProjects.length / PROJECT_LIST_PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const pagedProjects = useMemo(() => paginateProjects(sortedProjects, safePage), [safePage, sortedProjects])

  const deleteMutation = useMutation({
    mutationFn: (project: Project) => projectApi.delete(project.id),
    onSuccess: async () => {
      setDeleteTarget(null)
      toast.success(t('project.deleteMoved'))
      await queryClient.invalidateQueries({ queryKey: ['projects'] })
      await queryClient.invalidateQueries({ queryKey: ['projects-trash'] })
    },
    onError: (error) => toast.error(friendlyMessage(error, t('project.deleteFailed'))),
  })

  useEffect(() => {
    if (!sortOptions.some((option) => option.value === sortKey)) {
      setSortKey('updated')
    }
  }, [sortKey, sortOptions])

  const onSortChange = (value: ProjectSortKey) => {
    setSortKey(value)
    setPage(0)
  }

  return (
    <section className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="mb-2 flex items-center gap-2 text-sm text-primary">
            <Sparkles className="size-4" />
            {t('app.tagline')}
          </p>
          <h1 className="text-2xl font-semibold tracking-normal sm:text-3xl">{t('project.title')}</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{t('project.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={sortKey} onValueChange={(value) => onSortChange(value as ProjectSortKey)}>
            <SelectTrigger aria-label={t('project.sort.label')} className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => navigate('/novel-import')}>
            <FileUp className="size-4" />
            {t('novelImport.importNovel')}
          </Button>
          <Button onClick={() => setOpen(true)}>
            <Plus className="size-4" />
            {t('project.new')}
          </Button>
        </div>
      </div>

      <OnboardingChecklist userId={user?.user_id} />

      {projectsQuery.error ? (
        <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {friendlyMessage(projectsQuery.error, t('common.serviceError'))}
        </div>
      ) : null}

      {projectsQuery.isLoading ? (
        <ProjectSkeletonGrid />
      ) : sortedProjects.length ? (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {pagedProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                genreLabel={resolveProjectGenreLabel(project, templatesQuery.data, lang)}
                onOpen={(id) => navigate(`/projects/${id}`)}
                onDelete={setDeleteTarget}
              />
            ))}
          </div>
          <Pagination
            page={safePage}
            pageCount={pageCount}
            pageSize={PROJECT_LIST_PAGE_SIZE}
            total={sortedProjects.length}
            onPage={setPage}
          />
        </div>
      ) : (
        <div className="grid min-h-[360px] place-items-center rounded-lg border border-dashed bg-card/45 p-8 text-center">
          <div>
            <p className="text-lg font-medium">{t('project.empty')}</p>
            <p className="mt-2 text-sm text-muted-foreground">{t('project.emptyHint')}</p>
            <Button className="mt-5" onClick={() => setOpen(true)}>
              <Plus className="size-4" />
              {t('project.new')}
            </Button>
          </div>
        </div>
      )}

      <CreateProjectWizard open={open} onOpenChange={setOpen} userId={user?.user_id} />
      <Dialog open={Boolean(deleteTarget)} onOpenChange={(nextOpen) => {
        if (!nextOpen && !deleteMutation.isPending) setDeleteTarget(null)
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('project.deleteConfirmTitle')}</DialogTitle>
            <DialogDescription>
              {t('project.deleteConfirmDescription', { title: deleteTarget?.title ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="size-4" />
              {t('project.moveToTrash')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
